import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import provideDataIcon from '../../img/provide-data.svg';
import { PlusIcon } from '@heroicons/react/20/solid';
import { useNavigate, useLocation } from 'react-router-dom';
import StandardTable from '../../ui/molecules/StandardTable';
import TablePageLayout from '../../ui/molecules/TablePageLayout';
import { formatDisplayDateTime } from '../../utils/dateFormatting';
import useSessionContext from '../../hooks/useSessionContext';
import useDatasetProviders from '../../hooks/useDatasetProviders';
import { fetchDatasetsForWorkspace } from '../../services/datasetsApi';
import { Snackbar } from '../../ui/atoms';

const SkeletonRow = React.memo(() => (
  <tr className="animate-pulse">
    <td className="whitespace-nowrap py-5 pl-4 pr-3 sm:pl-0">
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
    </td>
    <td className="whitespace-nowrap px-3 py-5">
      <div className="h-6 bg-gray-200 rounded-full w-16"></div>
    </td>
    <td className="whitespace-nowrap px-3 py-5">
      <div className="h-4 bg-gray-200 rounded w-24"></div>
    </td>
    <td className="whitespace-nowrap px-3 py-5">
      <div className="h-4 bg-gray-200 rounded w-28"></div>
    </td>
  </tr>
));

const EmptyState = ({ onPrimaryClick }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center h-full text-center py-24">
      <img 
        src={provideDataIcon} 
        alt={t('datasets.addSource')} 
        className="mx-auto h-32 w-32 text-orange-400"
      />
      <h1 className="text-3xl font-medium text-gray-900 mt-6">{t('datasets.emptyState.title')}</h1>
      <p className="mt-1 text-base text-gray-800 opacity-80">{t('datasets.emptyState.description')}</p>
      <div className="mt-6">
        <button
          type="button"
          onClick={onPrimaryClick}
          className="btn btn-md btn-rounded btn-primary"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          {t('datasets.emptyState.button')}
        </button>
      </div>
    </div>
  );
};

const DatasetsTable = () => {
  const { t, i18n } = useTranslation();
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarState, setSnackbarState] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { token, workspaceId } = useSessionContext();
  const { getAvailabilityLabel, getProviderLabel } = useDatasetProviders();

  useEffect(() => {
    if (location.state?.snackbar) {
      setSnackbarState(location.state.snackbar);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const primaryClick = useCallback(() => navigate('/datasets/create-dataset'), [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const datasetsData = await fetchDatasetsForWorkspace(token, workspaceId);
        setDatasets(datasetsData || []);
      } catch (err) {
        console.error('Error in component:', err);
        setError(t('common.error_generic'));
      } finally {
        setLoading(false);
      }
    };

    // Only fetch data if we have workspaceId
    if (workspaceId) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [token, t, workspaceId]);

  const getDataTypeLabel = useCallback((type) => {
    return t(`datasets.dataTypes.${type}`, { defaultValue: t('datasets.dataTypes.unknown') });
  }, [t]);

  const formatOwner = useCallback((dataset) => {
    const first = dataset.user_created?.first_name?.trim();
    const last = dataset.user_created?.last_name?.trim();
    const fullName = [first, last].filter(Boolean).join(' ');
    return fullName || dataset.user_created?.email || '-';
  }, []);

  const columns = useMemo(() => [
    {
      key: 'name',
      title: t('datasets.datasetName'),
      render: (dataset) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/datasets/details/${dataset.id}`);
          }}
          className="font-medium text-primary-500 hover:text-primary-700 text-left"
        >
          {dataset.name}
        </button>
      ),
      headerClassName: 'py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0',
      cellClassName: 'whitespace-nowrap py-5 pl-4 pr-3 text-sm sm:pl-0'
    },
    {
      key: 'type',
      title: t('common.type'),
      render: (dataset) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {getDataTypeLabel(dataset.type)}
        </span>
      ),
      cellClassName: 'whitespace-nowrap px-3 py-5 text-sm text-gray-500'
    },
    {
      key: 'provider',
      title: 'Provider',
      render: (dataset) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
          {getProviderLabel(dataset)}
        </span>
      ),
      cellClassName: 'whitespace-nowrap px-3 py-5 text-sm text-gray-500'
    },
    {
      key: 'availability',
      title: 'Availability',
      render: (dataset) => getAvailabilityLabel(dataset),
      cellClassName: 'whitespace-nowrap px-3 py-5 text-sm text-gray-500'
    },
    {
      key: 'owner',
      title: t('common.createdBy'),
      render: (dataset) => formatOwner(dataset),
      cellClassName: 'whitespace-nowrap px-3 py-5 text-sm text-gray-500'
    },
    {
      key: 'date_created',
      title: t('common.creationDate'),
      render: (dataset) => formatDisplayDateTime(dataset.date_created, i18n.language),
      cellClassName: 'whitespace-nowrap px-3 py-5 text-sm text-gray-500'
    }
  ], [t, navigate, getDataTypeLabel, i18n.language, formatOwner, getAvailabilityLabel, getProviderLabel]);

  const tableData = useMemo(() => {
    return datasets.map(dataset => ({
      ...dataset
    }));
  }, [datasets]);
  const hasItems = tableData.length > 0;

  if (error) return (
    <div className="flex justify-center items-center h-64">
      <div className="text-red-500">{t('common.error')}: {error}</div>
    </div>
  );

  return (
    <TablePageLayout
      title={t('datasets.title')}
      description={t('datasets.description')}
      primaryAction={hasItems ? t('datasets.addSource') : undefined}
      onPrimaryAction={hasItems ? primaryClick : undefined}
    >
      {loading || hasItems ? (
        <StandardTable
          columns={columns}
          data={tableData}
          loading={loading}
          SkeletonRow={SkeletonRow}
          EmptyState={EmptyState}
          emptyStateProps={{ onPrimaryClick: primaryClick }}
          onRowClick={(dataset) => navigate(`/datasets/details/${dataset.id}`)}
        />
      ) : (
        <EmptyState onPrimaryClick={primaryClick} />
      )}
      {snackbarState && (
        <Snackbar
          message={snackbarState.message}
          type={snackbarState.type}
          actionName={t('common.close', { defaultValue: 'Close' })}
          onClose={() => setSnackbarState(null)}
        />
      )}
    </TablePageLayout>
  );
}

export default DatasetsTable;